// Glonet voice telemetry (2026-07-29) — the same diagnostic spine the Adventure
// Game uses (src/components/AdventureGame/telemetry.js), applied to the platform's
// OWN voice surfaces: onboarding/profile-by-voice, the affiliate intake, and the
// host intake.
//
// Why this exists: the happy-path flow docs (QA/happy-paths/) have to answer
// "did the agent respond inside a reasonable window?" with a NUMBER, not a
// feeling. Before this module the only timing globals in the frontend were
// __ADV_*__ and __WC_*__ — the platform's own voice flows were unmeasured.
//
// Three globals, read by the flow runners and asserted against budgets:
//   window.__GLONET_VOICE_TIMINGS__  [{surface, ms}]  listening -> speaking gap
//                                    = the member-felt response time
//   window.__GLONET_TOOL_TIMINGS__   [{surface, tool, ms}]  client-tool dispatch
//   window.__GLONET_UX_TIMINGS__     {name: ms}  one-shot milestones
//                                    (mic granted, session connected, first audio)
// Plus window.__GLONET_TELEMETRY__ — the full timestamped event ring.
//
// Telemetry must never break a live call: every export swallows its own errors.

const CAP = 2000;

// The member-felt budgets. VOICE is the agent's mouth opening after the member
// stops talking; TOOL is the 500ms response law carried over from the game.
export const VOICE_BUDGET_MS = 1000;
export const TOOL_BUDGET_MS = 500;

let t0 = null;

const flagOn = () => {
  try {
    if (localStorage.getItem("glonet_telemetry") === "1") return true;
    if (localStorage.getItem("glonet_telemetry") === "0") return false;
  } catch (e) { /* no storage */ }
  return process.env.NODE_ENV !== "production";
};

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

export function gvTelReset(label) {
  t0 = now();
  try {
    window.__GLONET_TELEMETRY__ = [];
    window.__GLONET_VOICE_TIMINGS__ = [];
    window.__GLONET_TOOL_TIMINGS__ = [];
    window.__GLONET_UX_TIMINGS__ = {};
    window.__GLONET_TEL_T0__ = t0;
  } catch (e) { /* no window */ }
  gvTel("session.reset", { label: label || "" });
}

export function gvTel(ev, data) {
  try {
    if (t0 == null) t0 = now();
    const rec = { t: Math.round(now() - t0), iso: new Date().toISOString(), ev, data: data || {} };
    const buf = (window.__GLONET_TELEMETRY__ = window.__GLONET_TELEMETRY__ || []);
    buf.push(rec);
    if (buf.length > CAP) buf.splice(0, buf.length - CAP);
    if (flagOn()) {
      // eslint-disable-next-line no-console
      console.debug(`[glonet-tel +${rec.t}ms] ${ev}`, data || "");
    }
    return rec;
  } catch (e) {
    return null;
  }
}

// A one-shot milestone, measured from session zero. Used for the things that
// happen once per flow step: mic granted, websocket connected, first audio.
export function gvMark(name) {
  try {
    if (t0 == null) t0 = now();
    const ms = Math.round(now() - t0);
    const m = (window.__GLONET_UX_TIMINGS__ = window.__GLONET_UX_TIMINGS__ || {});
    if (m[name] === undefined) m[name] = ms;   // first occurrence wins
    gvTel(`mark.${name}`, { ms });
    return ms;
  } catch (e) {
    return null;
  }
}

// Time an arbitrary async span and record it as a milestone (e.g. the whole
// startSession handshake). Returns whatever fn returns.
export async function gvTimeSpan(name, fn) {
  const start = now();
  try {
    return await fn();
  } finally {
    try {
      const ms = Math.round(now() - start);
      const m = (window.__GLONET_UX_TIMINGS__ = window.__GLONET_UX_TIMINGS__ || {});
      m[name] = ms;
      gvTel(`span.${name}`, { ms });
    } catch (e) { /* never throw */ }
  }
}

// Wrap a client tool so every dispatch is timed against the 500ms law. The flow
// runners read __GLONET_TOOL_TIMINGS__ and FAIL the step on a breach, exactly
// like the game's harnesses do with __ADV_TOOL_TIMINGS__.
export function gvTimedTools(surface, tools) {
  const out = {};
  Object.keys(tools || {}).forEach((tool) => {
    const fn = tools[tool];
    out[tool] = async (...args) => {
      const start = now();
      try {
        return await fn(...args);
      } finally {
        try {
          const ms = Math.round(now() - start);
          (window.__GLONET_TOOL_TIMINGS__ = window.__GLONET_TOOL_TIMINGS__ || []).push({ surface, tool, ms });
          gvTel("tool", { surface, tool, ms });
          if (ms > TOOL_BUDGET_MS) {
            // eslint-disable-next-line no-console
            console.warn(`[glonet-latency] ${surface}.${tool} ${ms}ms — over the ${TOOL_BUDGET_MS}ms budget`);
          }
        } catch (e) { /* never throw into a live call */ }
      }
    };
  });
  return out;
}

// The member-felt response time: how long after the member stopped talking did
// the agent's mouth open. Call from onModeChange. Returns a stateful recorder so
// each surface keeps its own listening clock.
//
// CORRECTED 2026-08-12. The clock used to start at mode === "listening", which
// is the moment the AGENT stops talking — not the moment the member stops. On
// a flow where the member replies instantly the two are nearly the same, which
// is why this read ~2.3s and looked right. Drive it with a scripted tape that
// leaves twelve seconds of silence between lines and the same code reports
// 6-9s: it was billing the agent for the member's thinking time.
//
// So: the recorder is returned with a `heard()` method. A surface that knows
// when the member's turn actually landed (onMessage with source "user") calls
// it, and the measured gap becomes "member's words arrived -> agent spoke".
// Surfaces that do not call it keep the old behaviour exactly, which is why
// this is safe to land under the flows that already depend on it.
export function gvModeRecorder(surface) {
  let listenStart = null;
  const rec = (mode) => {
    try {
      const n = now();
      if (mode === "speaking") {
        gvMark("first_audio");
        if (listenStart != null) {
          const ms = Math.round(n - listenStart);
          (window.__GLONET_VOICE_TIMINGS__ = window.__GLONET_VOICE_TIMINGS__ || []).push({ surface, ms });
          gvTel("voice.response", { surface, ms });
          if (ms > VOICE_BUDGET_MS) {
            // eslint-disable-next-line no-console
            console.warn(`[glonet-latency] ${surface} agent took ${ms}ms to start speaking (target <${VOICE_BUDGET_MS}ms)`);
          }
        }
      }
      listenStart = mode === "listening" ? n : null;
      gvTel("mode", { surface, mode });
    } catch (e) { /* never throw */ }
  };
  // The member's turn just landed — restart the clock from here, so what we
  // report is the wait THEY felt.
  rec.heard = () => {
    try { listenStart = now(); gvTel("heard", { surface }); }
    catch (e) { /* never throw */ }
  };
  return rec;
}

// Pretty dump for a live session / for pasting back after a walkthrough.
export function gvTelDump() {
  try {
    const buf = window.__GLONET_TELEMETRY__ || [];
    return buf.map((r) => `+${String(r.t).padStart(6)}ms  ${r.ev}  ${JSON.stringify(r.data)}`).join("\n");
  } catch (e) {
    return "";
  }
}

// A single object the flow runners can grab in one page.evaluate().
export function gvTelSummary() {
  try {
    const voice = window.__GLONET_VOICE_TIMINGS__ || [];
    const tools = window.__GLONET_TOOL_TIMINGS__ || [];
    const ms = voice.map((v) => v.ms).sort((a, b) => a - b);
    return {
      marks: { ...(window.__GLONET_UX_TIMINGS__ || {}) },
      voice: { count: voice.length, samples: voice,
               median: ms.length ? ms[Math.floor(ms.length / 2)] : null,
               max: ms.length ? ms[ms.length - 1] : null,
               over_budget: voice.filter((v) => v.ms > VOICE_BUDGET_MS).length },
      tools: { count: tools.length, samples: tools,
               over_budget: tools.filter((t) => t.ms > TOOL_BUDGET_MS).length },
      budgets: { voice_ms: VOICE_BUDGET_MS, tool_ms: TOOL_BUDGET_MS },
      events: (window.__GLONET_TELEMETRY__ || []).length,
    };
  } catch (e) {
    return null;
  }
}

try {
  if (typeof window !== "undefined") {
    window.gvTelDump = gvTelDump;
    window.gvTelSummary = gvTelSummary;
  }
} catch (e) { /* no window */ }
